import { Capacitor } from "@capacitor/core";
import { Dialog } from "@capacitor/dialog";
import * as Sentry from "@sentry/capacitor";
import { SIGN_OUT_DEVICE_ID_HEADER } from "@vidimo/shared/contracts";
import { clearCapacitorAuthToken } from "better-auth-capacitor/client";
import { toast } from "sonner";
import { interfaceTranslator } from "@/i18n/translate";
import { clearSharedFiles } from "@/lib/api-download";
import {
  AUTH_STORAGE_PREFIX,
  authClient,
  getNativeSessionToken,
} from "@/lib/auth-client";
import {
  discardWriteJournal,
  journaledWriteCount,
  startWriteJournal,
} from "@/lib/collections/write-journal";
import { liveActivity } from "@/lib/native/live-activity";
import { getDeviceId } from "@/lib/notifications/push-registration";
import {
  forgetCachedIdentity,
  readBootIdentityUserId,
} from "@/lib/offline-session";
import { powerSyncDb } from "@/lib/powersync";
import { appRoutes } from "@/lib/routes";
import {
  markPageReloading,
  reloadApp,
  setSigningOut,
} from "@/lib/sign-out-state";
import { leaveSupportSession, SUPPORT_SESSION } from "@/lib/support-session";

/**
 * The on-device PowerSync replica belongs to exactly one account.
 *
 * Front desks share phones, so the replica left behind by one account must
 * never render under the next: guest names, contacts and bookings would cross
 * tenants. Two things enforce it:
 *
 * - `signOutOfDevice` clears the replica as the session ends and leaves on a
 *   fresh page, so the sign-in screen the next person sees holds nothing of
 *   the account, on disk or in memory; the files it shared go best-effort
 *   (`clearSharedFiles`).
 * - `ensureReplicaBelongsTo` runs before any authenticated screen mounts and
 *   clears a replica recorded against another account. That covers the paths
 *   no sign-out button runs: a session that expired, was revoked, or was
 *   cleared along with the cookies.
 *
 * The owner record is a bare user id in localStorage, beside the offline
 * session identity (offline-session.ts); it is no secret, only a label for
 * whose rows the SQLite file holds.
 */

const REPLICA_OWNER_KEY = "superhost.replica.owner";

/** How long sign-out waits for queued writes to upload before asking. */
const FLUSH_TIMEOUT_MS = 10_000;
const FLUSH_POLL_MS = 250;

export function readReplicaOwner(): string | null {
  try {
    return window.localStorage.getItem(REPLICA_OWNER_KEY);
  } catch {
    return null;
  }
}

function writeReplicaOwner(userId: string): void {
  try {
    window.localStorage.setItem(REPLICA_OWNER_KEY, userId);
  } catch {
    // localStorage unavailable. `canRecordOwner` keeps that from turning into
    // an adopted replica: with no owner record possible, nothing is adopted.
  }
}

/**
 * Whether an owner record written now would be there to read later. Without
 * it, "no owner recorded" says nothing, and adopting on that basis would hand
 * every replica to whoever signs in next.
 */
function canRecordOwner(): boolean {
  const probeKey = `${REPLICA_OWNER_KEY}.probe`;
  try {
    window.localStorage.setItem(probeKey, "1");
    window.localStorage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
}

function forgetReplicaOwner(): void {
  try {
    window.localStorage.removeItem(REPLICA_OWNER_KEY);
  } catch {
    // ignore
  }
}

async function pendingUploadCount(timeoutMs: number): Promise<number> {
  const { count } = await withinCheckStep(
    powerSyncDb.getUploadQueueStats(),
    "upload queue read",
    timeoutMs
  );
  // Writes still waiting for SQLite are not queued for upload yet, and the
  // clear discards them all the same.
  return count + journaledWriteCount();
}

/**
 * Wipe the replica, its upload queue and the write journal without queueing
 * CRUD: plain deletes would replay as real server-side writes through the
 * upload queue. The lock-screen Live Activities and the taps queued on them go
 * too, since they show and act on the same account's tasks, and so do the files
 * it shared out of the app (`clearSharedFiles`, never awaited, so nothing waits
 * on it; after the clear, since iOS runs plugin calls one at a time).
 */
async function clearDeviceReplica(): Promise<void> {
  hasPageStartedClear = true;
  discardWriteJournal();
  await liveActivity.forgetAccount();
  await powerSyncDb.disconnectAndClear();
  forgetReplicaOwner();
  void clearSharedFiles();
}

interface ReplicaContents {
  /** Any row at all: synced, written locally, or queued to go up. */
  held: boolean;
  /** Writes queued for upload. */
  queued: number;
}

/**
 * What the replica holds. The data tables are read directly, not inferred
 * from the sync log: a row written here and already uploaded, but not yet
 * confirmed by a sync checkpoint, is in its data table with no log entry and
 * nothing queued, and it is still that account's row.
 */
async function readReplicaContents(): Promise<ReplicaContents> {
  const tables = await powerSyncDb.getAll<{ name: string }>(
    "SELECT name FROM sqlite_master WHERE type = 'table' AND name LIKE 'ps_data%'"
  );
  const dataRows = tables.map(
    ({ name }) => `EXISTS (SELECT 1 FROM "${name.replaceAll('"', '""')}")`
  );
  const anyData = dataRows.length > 0 ? dataRows.join(" OR ") : "0";
  const row = await powerSyncDb.get<{
    data: number;
    queued: number;
    synced: number;
  }>(
    `SELECT EXISTS (SELECT 1 FROM ps_oplog) AS synced, (SELECT count(*) FROM ps_crud) AS queued, (${anyData}) AS data`
  );
  return {
    held: row.synced === 1 || row.queued > 0 || row.data === 1,
    queued: row.queued,
  };
}

/**
 * How long one database step of the check may take before it counts as
 * failed. It only has to tell a hang from slowness, so it sits well above what
 * a healthy device takes: the first read after a reload opens the whole
 * database, measured at up to 25s on a loaded cloud VM with the seeded demo
 * replica, and a clear of that replica at up to 15s.
 */
const CHECK_STEP_TIMEOUT_MS = 60_000;

/**
 * Reject if `promise` has not settled within `timeoutMs` (by default the
 * check's step limit). A database or request that waits instead of failing
 * would otherwise hold its caller for good; as a rejection it takes the
 * caller's failure path: the gate reports and retries it, the sign-out's flush
 * keeps the last count it read, and the sign-out asks whether its session
 * ended anyway.
 */
async function withinCheckStep<T>(
  promise: Promise<T>,
  step: string,
  timeoutMs = CHECK_STEP_TIMEOUT_MS
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`replica ${step} timed out`)),
      timeoutMs
    );
  });
  try {
    return await Promise.race([promise, timedOut]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * What this page's memory may hold. The PowerSync-backed collections keep
 * what they read for the life of the page. So a page that has shown one
 * account, or that has started clearing the replica at all (a clear that timed
 * out can still finish later), cannot vouch for its memory: it reloads before
 * it shows anyone else. Both facts reset only with the page.
 */
let renderedAccount: string | null = null;
let hasPageStartedClear = false;

export function markReplicaRendered(userId: string): void {
  renderedAccount = userId;
  startWriteJournal(userId, readReplicaOwner);
}

/** Whether this page must reload before it may render `userId`. */
export function mustReloadBeforeRendering(userId: string): boolean {
  const hasShownAnotherAccount =
    renderedAccount !== null && renderedAccount !== userId;
  return hasShownAnotherAccount || hasPageStartedClear;
}

export type ReplicaCheck = "ready" | "reloading";

/**
 * Make the replica safe to render for `userId`. Answers `"reloading"` when the
 * page has to start over, and then renders nothing more.
 *
 * - Already theirs: kept.
 * - No owner recorded, and nothing says it is anyone else's: adopted. That is
 *   a fresh install, a replica the last sign-out already cleared, or one left
 *   by a build that recorded no owner. A healthy replica is never discarded on
 *   a guess, so the last case keeps it unless the page booted with a different
 *   account as the cached identity (offline-session.ts), which predates any
 *   sign-in on this page. Nothing is adopted where no owner record can be
 *   kept (`canRecordOwner`). The price: a device that one account signed out of
 *   on such a build, and another signs in to with no cached identity left,
 *   shows the first account's rows until the first sync replaces them, once.
 * - Holding nothing: recorded as theirs, with nothing to clear.
 * - Anything else (another account's): cleared, and the page reloaded.
 * - On a page whose memory cannot be vouched for (`mustReloadBeforeRendering`),
 *   whatever the owner record says: handed over on a fresh page.
 *
 * Each database step is time-boxed (`withinCheckStep`), so a check can fail
 * but never hang; the gate retries a failure and reports it, and never acts
 * on one destructively.
 *
 * The reload is not optional. The PowerSync-backed collections start reading
 * the replica into memory the moment their modules load
 * (`@tanstack/powersync-db-collection` creates them with `startSync: true`),
 * which is before this check can run, and the screens render the rows they
 * read before the clear's deletes reach them. The reload finds the owner
 * recorded and nothing to clear, so it cannot loop.
 *
 * Writes queued in a replica that is not this account's are discarded with it:
 * uploading them under this account's token would attribute another account's
 * edits to it. The count goes to Sentry so the loss is visible to us; the row
 * payloads never do.
 */
export async function ensureReplicaBelongsTo(
  userId: string
): Promise<ReplicaCheck> {
  // One check at a time, whatever the account: two running together would
  // each clear and reload, and whichever wrote last would name the owner.
  // A second caller for the same account shares the running check.
  while (inFlightCheck && inFlightCheck.userId !== userId) {
    try {
      // biome-ignore lint/performance/noAwaitInLoops: each wait is for the check running now; the next one may start behind it.
      await inFlightCheck.promise;
    } catch {
      // That check's own caller handles its failure.
    }
  }
  if (isReloadPending) {
    return "reloading";
  }
  if (inFlightCheck) {
    return inFlightCheck.promise;
  }
  const promise = runReplicaCheck(userId);
  inFlightCheck = { promise, userId };
  try {
    return await promise;
  } finally {
    if (inFlightCheck?.promise === promise) {
      inFlightCheck = null;
    }
  }
}

let inFlightCheck: { promise: Promise<ReplicaCheck>; userId: string } | null =
  null;

/**
 * Set once a check has cleared the replica and asked for a reload. The new
 * owner is already recorded by then, so until the page actually unloads,
 * nothing may read that record as permission to render what memory still
 * holds.
 */
let isReloadPending = false;

export function isReplicaReloadPending(): boolean {
  return isReloadPending;
}

async function runReplicaCheck(userId: string): Promise<ReplicaCheck> {
  const owner = readReplicaOwner();
  // Checked first, even over an owner record that already names this
  // account: another tab can write that record while this page still holds
  // the account it showed before.
  if (mustReloadBeforeRendering(userId)) {
    return await reloadForAccount(userId, owner);
  }
  if (owner === userId) {
    return "ready";
  }
  const bootIdentity = readBootIdentityUserId();
  const isUnrecordedAndUncontested =
    owner === null &&
    (bootIdentity === null || bootIdentity === userId) &&
    canRecordOwner();
  if (isUnrecordedAndUncontested) {
    writeReplicaOwner(userId);
    return "ready";
  }
  const contents = await withinCheckStep(readReplicaContents(), "read");
  if (!contents.held) {
    writeReplicaOwner(userId);
    return "ready";
  }
  return await reloadForAccount(userId, owner, contents);
}

/**
 * Hand the replica to `userId` on a fresh page: clear it first unless it is
 * already theirs, record them as its owner, and reload. The fresh page has
 * shown no one and started no clear, so its own check cannot come back here.
 */
async function reloadForAccount(
  userId: string,
  owner: string | null,
  knownContents?: ReplicaContents
): Promise<ReplicaCheck> {
  if (owner !== userId) {
    const { held, queued } =
      knownContents ?? (await withinCheckStep(readReplicaContents(), "read"));
    if (queued > 0) {
      Sentry.captureMessage(
        "[replica] discarded another account's queued writes",
        {
          extra: { discarded: queued, ownerKnown: owner !== null },
          level: "warning",
        }
      );
    }
    if (held) {
      await withinCheckStep(clearDeviceReplica(), "clear");
    }
  }
  writeReplicaOwner(userId);
  isReloadPending = true;
  reloadApp();
  return "reloading";
}

/** Queued writes that could not be counted in time: ask, as for a known count. */
const UNKNOWN_UNSYNCED = -1;

/**
 * Wait for the upload queue to drain, up to `FLUSH_TIMEOUT_MS` in all, and
 * return how many writes are still queued. Each read gets only what is left of
 * the budget; one that cannot finish in it keeps the last count read, or
 * `UNKNOWN_UNSYNCED` when none was (a database still opening, say). Waits out
 * the whole budget even while the sync stream reads disconnected: uploads go
 * through the API on their own, and the stream drops for a moment on every
 * reconnect.
 */
async function flushPendingUploads(): Promise<number> {
  const deadline = Date.now() + FLUSH_TIMEOUT_MS;
  let remaining = UNKNOWN_UNSYNCED;
  const readWithinBudget = async (): Promise<number> => {
    try {
      return await pendingUploadCount(Math.max(deadline - Date.now(), 1));
    } catch {
      return remaining;
    }
  };
  remaining = await readWithinBudget();
  while (remaining > 0 && Date.now() < deadline) {
    // biome-ignore lint/performance/noAwaitInLoops: polling the queue, each read must follow the previous wait.
    await new Promise((resolve) => setTimeout(resolve, FLUSH_POLL_MS));
    remaining = await readWithinBudget();
  }
  return remaining;
}

/** What the replica is cleared for, which picks the dialog's words. */
type DiscardAction = "clearDatabase" | "signOut";

/**
 * Give queued writes a chance to upload, then ask before an action that
 * clears the replica discards whatever is still queued. Resolves whether the
 * action may go ahead.
 */
export async function mayDiscardUnsynced(
  action: DiscardAction
): Promise<boolean> {
  const unsynced = await flushPendingUploads();
  if (unsynced === 0) {
    return true;
  }
  const t = interfaceTranslator("shell.deviceReplica");
  const { value } = await Dialog.confirm({
    cancelButtonTitle: t(`${action}.cancel`),
    message:
      unsynced === UNKNOWN_UNSYNCED
        ? t(`${action}.unsyncedUnknown`)
        : t(`${action}.unsynced`, { count: unsynced }),
    okButtonTitle: t(`${action}.confirm`),
    title: t("unsyncedTitle"),
  });
  return value;
}

/**
 * How long a sign-out waits for the server before giving up and keeping the
 * person signed in. A request that never answers (a captive portal, a link
 * that drops mid-request) would otherwise leave the sign-out spinning.
 */
const SIGN_OUT_REQUEST_TIMEOUT_MS = 60_000;

/** How long a failed sign-out waits to learn whether the session ended anyway. */
const SESSION_CHECK_TIMEOUT_MS = 10_000;

type SignOutOutcome = "signed-out" | "cancelled" | "failed";

/**
 * Sign out and leave nothing of this account on the device.
 *
 * Order matters:
 *
 * 1. Offline, nothing starts: the server has to end the session, and a local
 *    wipe of a session that stays live would lose data and sign no one out.
 * 2. The app is held (sign-out-state.ts), then queued writes flush while the
 *    session can still upload them; whatever cannot upload is put to the user
 *    before it is lost.
 * 3. The session ends on the server, which drops the push tokens it
 *    registered. The auth client discards the device's copy only once the
 *    server confirms (patches/better-auth-capacitor). A request that fails or
 *    gets no answer within `SIGN_OUT_REQUEST_TIMEOUT_MS` is settled by asking
 *    whether the session still exists: gone, the sign-out goes on; still
 *    there, the device is left exactly as it was.
 * 4. The replica and the cached offline identity are cleared, and the page is
 *    replaced by a fresh sign-in page, so nothing of the account stays in
 *    memory either. A clear that fails keeps its owner record, so the next
 *    sign-in's `ensureReplicaBelongsTo` clears it before anything renders.
 *
 * One sign-out at a time: a second tap while one runs gets the same outcome,
 * not a second dialog and a second request.
 */
export async function signOutOfDevice(): Promise<SignOutOutcome> {
  if (inFlightSignOut) {
    return inFlightSignOut;
  }
  const promise = runSignOut();
  inFlightSignOut = promise;
  try {
    return await promise;
  } finally {
    if (inFlightSignOut === promise) {
      inFlightSignOut = null;
    }
  }
}

let inFlightSignOut: Promise<SignOutOutcome> | null = null;

async function runSignOut(): Promise<SignOutOutcome> {
  if (!navigator.onLine) {
    toast.error(interfaceTranslator("shell.deviceReplica")("signOutOffline"));
    return "failed";
  }

  setSigningOut(true);
  let outcome: SignOutOutcome;
  try {
    outcome = await endSession();
  } catch (err) {
    console.error("[replica] sign-out failed:", err);
    outcome = "failed";
  }
  if (outcome !== "signed-out") {
    setSigningOut(false);
    if (outcome === "failed") {
      toast.error(interfaceTranslator("shell.deviceReplica")("signOutFailed"));
    }
    return outcome;
  }
  forgetCachedIdentity();
  await clearDeviceReplicaWithin();
  leaveForSignInPage();
  return "signed-out";
}

async function endSession(): Promise<SignOutOutcome> {
  const isDiscardAccepted = await mayDiscardUnsynced("signOut");
  if (!isDiscardAccepted) {
    return "cancelled";
  }
  // Sent without its token, a sign-out or session check answers "no session"
  // for any device, which would read as a sign-out that happened.
  const isTokenMissing =
    Capacitor.isNativePlatform() && (await getNativeSessionToken()) === null;
  if (isTokenMissing) {
    return "failed";
  }
  const abort = new AbortController();
  try {
    const { error } = await withinCheckStep(
      authClient.signOut({
        fetchOptions: { headers: await deviceHeaders(), signal: abort.signal },
      }),
      "sign-out request",
      SIGN_OUT_REQUEST_TIMEOUT_MS
    );
    if (!error) {
      return "signed-out";
    }
  } catch {
    abort.abort();
  }
  return (await isSessionOver()) ? "signed-out" : "failed";
}

/** Names this device so the server drops its tokens with the session. */
async function deviceHeaders(): Promise<Record<string, string>> {
  if (!Capacitor.isNativePlatform()) {
    return {};
  }
  try {
    return { [SIGN_OUT_DEVICE_ID_HEADER]: await getDeviceId() };
  } catch (err) {
    Sentry.captureException(err);
    return {};
  }
}

/**
 * Whether the server no longer holds this device's session: a sign-out it
 * completed though its answer never arrived. The auth client drops its stored
 * session only on the answer, so it is dropped here instead.
 */
async function isSessionOver(): Promise<boolean> {
  try {
    const { data, error } = await withinCheckStep(
      authClient.getSession(),
      "session check",
      SESSION_CHECK_TIMEOUT_MS
    );
    if (error || data) {
      return false;
    }
  } catch {
    return false;
  }
  await forgetEndedSession();
  return true;
}

/** How long an erased account's sign-out waits to reach this browser's tabs. */
const ERASED_SIGN_OUT_TIMEOUT_MS = 10_000;

/**
 * Sign this device out of an account the server has just erased, with its
 * sessions: the sign-out request tells this browser's other tabs, and the
 * device's copy of the session goes whether or not it answers.
 */
export async function signOutErasedAccount(): Promise<void> {
  await settlesWithin(authClient.signOut(), ERASED_SIGN_OUT_TIMEOUT_MS);
  await forgetEndedSession();
}

/**
 * Drop this device's copy of a session the server has already ended, as the
 * auth client does on a confirmed sign-out: the stored token, and the session
 * in memory, so the signed-in shell unmounts and runs its teardown.
 */
async function forgetEndedSession(): Promise<void> {
  await clearCapacitorAuthToken({ storagePrefix: AUTH_STORAGE_PREFIX });
  const { session } = authClient.$store.atoms;
  session.set({ ...session.get(), data: null, error: null, isPending: false });
}

/**
 * How long a sign-out waits for the replica to clear before leaving anyway.
 * Well above a healthy clear, measured at up to 15s for the seeded demo
 * replica on a loaded cloud VM: giving up on a slow but healthy clear leaves
 * the account on disk for the next sign-in to clean up.
 */
const CLEAR_TIMEOUT_MS = 60_000;

/**
 * Clear the replica for a session that is ending, waiting at most
 * `CLEAR_TIMEOUT_MS`: the person leaving is never held by a database that will
 * not answer. A replica that did not clear keeps its owner record, so the next
 * sign-in's check clears it before rendering anything.
 */
export async function clearDeviceReplicaWithin(): Promise<void> {
  const isCleared = await settlesWithin(clearDeviceReplica(), CLEAR_TIMEOUT_MS);
  if (!isCleared) {
    Sentry.captureMessage(
      "[replica] session ended with the replica uncleared",
      {
        level: "warning",
      }
    );
  }
}

/**
 * How long a sign-out waits for its page load to take effect before giving
 * the sign-in form back. The unsaved-changes guards stand down for this load
 * (`markPageReloading`), so this only fires if something else cancels it; the
 * session is over either way, and a spinner with nothing behind it is the
 * worse screen to leave someone on.
 */
const NAVIGATION_FALLBACK_MS = 8000;

export function leaveForSignInPage(): void {
  markPageReloading();
  // A support tab has no sign-in page to go back to: it ends (ADR-0143).
  if (SUPPORT_SESSION) {
    leaveSupportSession();
    return;
  }
  window.location.assign(appRoutes.login());
  setTimeout(() => setSigningOut(false), NAVIGATION_FALLBACK_MS);
}

/** Whether `promise` settles, either way, within `ms`. */
async function settlesWithin(
  promise: Promise<unknown>,
  ms: number
): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<false>((resolve) => {
    timer = setTimeout(() => resolve(false), ms);
  });
  const settled = (async () => {
    try {
      await promise;
    } catch (err) {
      Sentry.captureException(err);
    }
    return true as const;
  })();
  try {
    return await Promise.race([settled, timedOut]);
  } finally {
    clearTimeout(timer);
  }
}
